// MCP Security — Top-N panel card.
//
// Visual family-match with the WAF Dashboard top-N panels (see
// src/components/pages/_new/services/web_application/Dashboard.js —
// the "Top WAF detections by country" / "Top user agent" / "Top paths"
// cards). Same border, radius, header typography, and 42 px row with
// indigo-alpha hover.
//
// Rows:
//   { key, label, value, cc?, chips?: string[], badge?, pill?, pillBg?,
//     pillFg?, sub?, onClick? }

import React from "react";
import { Box, Stack, Typography, Skeleton } from "@mui/material";
import tokens from "./tokens";
import CountryFlag from "./CountryFlag";
import AttackChips from "./AttackChips";

export default function TopPanel({
  title,
  rows = [],
  isNew,
  onViewAll,
  loading,
  emptyLabel = "No data in this window.",
  maxRows = 6,
  sx,
}) {
  return (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${tokens.neutral[200]}`,
        borderRadius: tokens.cardRadius,
        p: 2,
        pb: 1,
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
        <Typography
          sx={{
            flex: 1,
            fontSize: 14,
            lineHeight: "21px",
            fontWeight: "medium",
            color: tokens.neutral[1000],
            pl: 1,
          }}
        >
          {title}
        </Typography>
        {isNew && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 500,
              padding: "1px 6px",
              borderRadius: 999,
              background: tokens.primary[100],
              color: tokens.primaryIndigoInk,
              whiteSpace: "nowrap",
            }}
          >
            New
          </span>
        )}
        {onViewAll && (
          <Typography
            onClick={onViewAll}
            sx={{
              fontSize: 12,
              color: tokens.primaryIndigo,
              cursor: "pointer",
              pr: 1,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View all
          </Typography>
        )}
      </Stack>
      {loading && (
        <Stack spacing={1} sx={{ px: 1 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={20} />
          ))}
        </Stack>
      )}
      {!loading && rows.length === 0 && (
        <Typography sx={{ fontSize: 12, color: tokens.neutral[700], py: 2, textAlign: "center" }}>
          {emptyLabel}
        </Typography>
      )}
      {!loading &&
        rows.slice(0, maxRows).map((row, idx) => (
          <Stack
            key={row.key ?? idx}
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={row.onClick}
            sx={{
              p: 2,
              height: 42,
              borderRadius: "8px",
              cursor: row.onClick ? "pointer" : "default",
              "&:hover": row.onClick ? { backgroundColor: tokens.primary.alpha } : {},
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                {row.cc && <CountryFlag cc={row.cc} />}
                <Typography
                  sx={{
                    fontFamily: tokens.fontMono,
                    fontSize: 13,
                    lineHeight: "21px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: tokens.neutral[1000],
                    minWidth: 0,
                  }}
                >
                  {row.label}
                </Typography>
                {row.badge && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      padding: "0 6px",
                      borderRadius: 999,
                      background: tokens.warning.bg,
                      color: tokens.secondary[500],
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.badge}
                  </span>
                )}
              </Stack>
              {row.chips && row.chips.length > 0 && (
                <Box sx={{ mt: 0.25 }}>
                  <AttackChips classes={row.chips} dense limit={5} />
                </Box>
              )}
              {row.sub && (
                <Typography sx={{ fontSize: 11, color: tokens.neutral[700] }}>{row.sub}</Typography>
              )}
            </Box>
            {row.pill && (
              <span
                style={{
                  fontSize: 11,
                  padding: "1px 8px",
                  borderRadius: 999,
                  background: row.pillBg || tokens.neutral[150],
                  color: row.pillFg || tokens.neutral[800],
                  whiteSpace: "nowrap",
                }}
              >
                {row.pill}
              </span>
            )}
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: "21px",
                color: tokens.neutral[600],
                fontVariantNumeric: "tabular-nums",
                minWidth: 48,
                textAlign: "right",
              }}
            >
              {typeof row.value === "number" ? row.value.toLocaleString() : row.value}
            </Typography>
          </Stack>
        ))}
    </Box>
  );
}
